import React from 'react';
import { motion } from 'framer-motion';
import {
  FileSearch, GitCompareArrows, BookOpenCheck,
  ShieldAlert, FileStack, UserCheck,
} from 'lucide-react';
import { Reveal, StaggeredGroup, StaggeredItem } from './Reveal';

const features = [
  {
    icon: FileSearch,
    title: 'Missing document detection',
    desc: 'Required and recommended documents flagged before submission, by lane and product.',
  },
  {
    icon: GitCompareArrows,
    title: 'Cross-document validation',
    desc: 'Reconcile quantities, weights, consignees and HS codes across commercial, transport and regulatory documents.',
  },
  {
    icon: BookOpenCheck,
    title: 'Source-backed guidance',
    desc: 'Every recommendation cites the EU regulation, customs circular or trade rule it derives from.',
  },
  {
    icon: ShieldAlert,
    title: 'Pre-shipment risk detection',
    desc: 'Surface blocking risks and advisory warnings during review — never after customs.',
  },
  {
    icon: FileStack,
    title: 'Compliance report generation',
    desc: 'Auditable, shareable pre-submission summaries for internal reviewers and brokers.',
  },
  {
    icon: UserCheck,
    title: 'Human review workflow',
    desc: 'AI interprets and rules decide — but every action requires a human reviewer to confirm.',
  },
];

export default function FeaturesSection() {
  return (
    <section id="features" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <Reveal className="max-w-3xl mb-14 md:mb-20">
          <div className="text-xs uppercase tracking-[0.3em] text-cyan-glow font-mono mb-4">Why ComplyTradeIQ</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight">
            Operational compliance, not generic document automation
          </h2>
          <p className="mt-4 text-slate-400 text-base md:text-lg leading-relaxed max-w-2xl">
            Six capabilities built specifically for trade operations teams handling multi-document shipments under regulatory deadlines.
          </p>
        </Reveal>

        <StaggeredGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" stagger={0.06}>
          {features.map((f, i) => (
            <StaggeredItem key={f.title} className="group">
              <motion.div
                whileHover={{ y: -4, borderColor: 'rgba(56, 189, 248, 0.35)' }}
                transition={{ duration: 0.25 }}
                className="rounded-xl border border-white/10 bg-navy-900 p-7 h-full transition-colors"
                data-testid={`feature-${i}`}
              >
                <div className="h-11 w-11 rounded-lg bg-cyan-glow/10 border border-cyan-glow/20 grid place-items-center text-cyan-glow mb-5 group-hover:bg-cyan-glow/20 transition-colors">
                  <f.icon className="h-5 w-5" />
                </div>
                <div className="font-display text-lg font-semibold text-white">{f.title}</div>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.desc}</p>
              </motion.div>
            </StaggeredItem>
          ))}
        </StaggeredGroup>
      </div>
    </section>
  );
}
